import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import useApi from "../../../core/customhook/useApi";

interface ODLeavePieChartProps {
    financial_year: string;
}

const ODLeavePieChart: React.FC<ODLeavePieChartProps> = ({ financial_year }) => {
    const formatFinancialYear = (year: number) => {
        const nextYearShort = (year + 1).toString().slice(-2);
        return `${year}-${nextYearShort}`;
    };

    const getFinancialYear = () => {
        const today = new Date();
        const year = today.getFullYear();
        return formatFinancialYear(year);
    };

    const [finance_year, setFinance_year] = useState(getFinancialYear());

    // 🔹 API call
    const {
        data: monthlyLeaveData,
        loading: leave_balance_loading,
        error: leave_balance_error,
    } = useApi("/api/od-chart", {
        method: "POST",
        payload: { financial_year },
        autoCall: true,
        dependencies: [financial_year],
    });

    // 🔹 Transform API data → chart format
    const [months, setMonths] = useState<string[]>([]);
    const [appliedValues, setAppliedValues] = useState<number[]>([]);
    const [approvedValues, setApprovedValues] = useState<number[]>([]);

    useEffect(() => {
        if (monthlyLeaveData?.data) {
            const monthMap: Record<string, string> = {
                jan: "Jan",
                feb: "Feb",
                mar: "Mar",
                apr: "Apr",
                may: "May",
                jun: "Jun",
                jul: "Jul",
                aug: "Aug",
                sep: "Sep",
                oct: "Oct",
                nov: "Nov",
                dec: "Dec",
            };

            const orderedKeys = Object.keys(monthMap);

            const applied: number[] = [];
            const approved: number[] = [];
            const monthLabels: string[] = [];

            orderedKeys.forEach((key) => {
                const item = monthlyLeaveData.data[key];
                if (item) {
                    monthLabels.push(monthMap[key]);
                    applied.push(item.applied);
                    approved.push(item.approved);
                }
            });

            setMonths(monthLabels);
            setAppliedValues(applied);
            setApprovedValues(approved);
        }
    }, [monthlyLeaveData]);

    // 🔹 ApexCharts options
    const options: ApexCharts.ApexOptions = {
        chart: {
            type: "bar",
            stacked: false,
            toolbar: { show: false },
        },
        colors: ["#2e3294", "#f79622"], // Applied = yellow, Approved = green
        plotOptions: {
            bar: {
                columnWidth: "40%",
                dataLabels: { position: "top" },
            },
        },
        dataLabels: {
            enabled: true,
            formatter: (val) => (typeof val === "number" ? val.toString() : ""),
            offsetY: -20,
            style: {
                fontSize: "12px",
                colors: ["#555"],
            },
        },
        xaxis: {
            categories: months,
            position: "bottom",
            labels: {
                style: { fontSize: "12px" },
            },
        },
        yaxis: {
            min: 0,
            // max: 6, 
            tickAmount: 6, // force 1 step each
            labels: {
                show: true,
                formatter: (val) => val.toFixed(0), // avoid decimals
            },
        },
        legend: {
            position: "top",
            horizontalAlign: "center",
        },
        grid: {
            show: false,
        },
    };

    const series = [
        { name: "Applied", data: appliedValues },
        { name: "Approved", data: approvedValues },
    ];

    return (
        <div
            style={{
                width: "100%",
                maxWidth: "1000px",
                margin: "auto",
                textAlign: "center",
            }}
        >
            <h4
                style={{
                    textAlign: "center",
                    margin: "50px 0px 20px 0",
                    position: "relative",
                    display: "inline-block",
                    paddingBottom: "8px",
                }}
            >
                <strong>Monthly Out Duty Summary</strong>
                <span
                    style={{
                        content: "''",
                        position: "absolute",
                        left: 0,
                        bottom: 0,
                        height: "3px",
                        width: "100%",
                        backgroundColor: "#fbc02d", // underline color
                        borderRadius: "2px",
                    }}
                />
            </h4>

            <Chart options={options} series={series} type="bar" height={250} />
        </div>
    );
};

export default ODLeavePieChart;

